import {Check, Eye, EyeOff} from 'lucide-react';
import {Badge, Button} from '@kroll-resolver-crisp/ui-components-react';

export function WorkflowStatusBadge({
  status,
  enabled
}: {
  readonly status: string;
  readonly enabled: boolean;
}) {
  if (status === 'archived') {
    return <Badge variant="outline">Archived</Badge>;
  }

  if (status === 'draft') {
    return <Badge variant="secondary">Not approved</Badge>;
  }

  return enabled ? (
    <Badge>
      <Check aria-hidden className="size-3" />
      Live
    </Badge>
  ) : (
    <Badge variant="outline">
      <EyeOff aria-hidden className="size-3" />
      Disabled
    </Badge>
  );
}

type StepState = 'done' | 'current' | 'todo';

const STEP_STYLE = {
  done: 'bg-primary text-primary-foreground',
  current: 'ring-primary text-primary ring-2 ring-inset',
  todo: 'text-muted-foreground ring-border ring-1 ring-inset'
} as const;

export function SetupSteps({
  workflowKey,
  hasProposal,
  proposalAuthor,
  canContribute,
  onConfigure,
  onReview
}: {
  readonly workflowKey: string;
  readonly hasProposal: boolean;
  readonly proposalAuthor: string | null;
  readonly canContribute: boolean;
  readonly onConfigure: () => void;
  readonly onReview: () => void;
}) {
  const author = proposalAuthor ?? 'the author';
  const steps: Array<{
    title: string;
    detail: string;
    state: StepState;
    action?: React.ReactNode;
  }> = [
    {title: 'Create the draft', detail: `Key ${workflowKey}`, state: 'done'},
    {
      title: 'Set scope and routing',
      detail: hasProposal
        ? `Submitted${proposalAuthor ? ` by ${proposalAuthor}` : ''}`
        : 'Pick the root concept and write the routing definition.',
      state: hasProposal ? 'done' : 'current',
      action:
        !hasProposal && canContribute ? (
          <Button size="sm" onClick={onConfigure}>
            Configure guidance
          </Button>
        ) : null
    },
    {
      title: 'Independent approval',
      detail: hasProposal
        ? `Someone other than ${author} approves it. RD starts routing on its next load.`
        : 'An approver who didn’t submit it signs it off.',
      state: hasProposal ? 'current' : 'todo',
      action: hasProposal ? (
        <Button size="sm" variant="outline" onClick={onReview}>
          <Eye aria-hidden className="size-4" />
          Open review
        </Button>
      ) : null
    },
    {
      title: 'Add grounding',
      detail: 'Definitions, examples and keywords for each concept — one by one or by CSV.',
      state: 'todo'
    }
  ];

  return (
    <section
      aria-label="Setup"
      className="border-border bg-card flex flex-col gap-2 rounded-lg border px-4 py-3">
      <div>
        <h3 className="text-sm font-semibold">Not served yet</h3>
        <p className="text-muted-foreground text-xs">
          RD only routes to guidance once its first configuration is approved.
        </p>
      </div>
      <ol className="divide-border m-0 flex list-none flex-col divide-y p-0">
        {steps.map((step, index) => (
          <li key={step.title} className="flex items-center gap-3 py-2.5">
            <span
              className={`grid size-6 shrink-0 place-items-center rounded-full text-[11px] font-semibold ${STEP_STYLE[step.state]}`}>
              {step.state === 'done' ? <Check aria-hidden className="size-3" /> : index + 1}
            </span>
            <span className="flex min-w-0 flex-1 flex-col">
              <span
                className={`text-sm font-medium ${step.state === 'todo' ? 'text-muted-foreground' : ''}`}>
                {step.title}
              </span>
              <span className="text-muted-foreground text-xs">{step.detail}</span>
            </span>
            {step.action ?? null}
          </li>
        ))}
      </ol>
    </section>
  );
}
